import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dumbbell, ArrowLeft, Download, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function WorkoutImport() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workouts/by-token/${token}`);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Couldn't load this workout");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const importIt = async () => {
    setImporting(true);
    try {
      const { data: saved } = await api.post(`/workouts/by-token/${token}/import`);
      const added = saved.new_exercises_added || 0;
      const msg = added > 0
        ? `"${saved.name}" added — ${added} new exercise${added === 1 ? "" : "s"} also dropped into your library`
        : `"${saved.name}" added to your library`;
      toast.success(msg);
      nav(`/workouts/${saved.workout_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import");
    } finally {
      setImporting(false);
    }
  };

  if (loading) return (
    <div className="max-w-2xl mx-auto p-6 text-neutral-400 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (err) return (
    <div className="max-w-2xl mx-auto p-6">
      <div data-testid="wk-import-error" className="border border-neutral-800 p-6 text-center">
        <Dumbbell className="w-8 h-8 text-neutral-600 mx-auto mb-2" />
        <div className="text-sm text-neutral-300">{err}</div>
        <Link to="/library" className="text-xs text-blue-500 uppercase tracking-widest mt-3 inline-block">Back to Library</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} data-testid="wk-import-back" className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold flex items-center gap-2 mb-1">
        <Dumbbell className="w-3 h-3" /> SHARED WORKOUT
      </div>
      <h1 data-testid="wk-import-name" className="font-display text-4xl sm:text-5xl uppercase leading-none mb-2">{data.name}</h1>
      <div className="text-sm text-neutral-400 mb-1">By <span className="text-blue-500">{data.owner_name}</span>{data.owner_gym ? ` · ${data.owner_gym}` : ""}</div>
      {data.description && <div className="text-sm text-neutral-300 mt-3 mb-4">{data.description}</div>}

      <div className="grid grid-cols-3 gap-2 my-4 text-center">
        <div className="border border-neutral-800 p-3">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Category</div>
          <div className="font-display text-base text-white mt-1 truncate">{data.category}</div>
        </div>
        <div className="border border-neutral-800 p-3">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Exercises</div>
          <div className="font-display text-2xl text-blue-500 mt-1">{(data.exercises || []).length}</div>
        </div>
        <div className="border border-neutral-800 p-3">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Rest</div>
          <div className="font-display text-2xl text-white mt-1">{data.rest_seconds}<span className="text-xs text-neutral-500"> s</span></div>
        </div>
      </div>

      {data.missing_exercises > 0 && (
        <div data-testid="wk-import-missing-banner" className="border border-yellow-500/40 bg-yellow-500/5 p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-200">
            <span className="font-bold">{data.missing_exercises}</span> exercise{data.missing_exercises === 1 ? "" : "s"} from this workout
            {data.missing_exercises === 1 ? " isn't" : " aren't"} in your library yet — {data.missing_exercises === 1 ? "it" : "they"}'ll be added automatically when you import.
          </div>
        </div>
      )}

      <div className="border border-neutral-800 mb-5">
        {(data.exercises || []).map((e, i) => (
          <div key={i} data-testid={`wk-import-ex-${i}`} className="flex items-center gap-3 p-3 border-b border-neutral-800 last:border-b-0">
            <div className="w-8 h-8 border border-blue-500/40 bg-blue-500/5 flex items-center justify-center text-xs font-display text-blue-500">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base uppercase truncate">{e.name}</div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
                {(e.sets || []).length} set{(e.sets || []).length === 1 ? "" : "s"}
                {e.rest_seconds ? ` · rest ${e.rest_seconds}s` : ""}
                {e.tracking_unit === "distance" ? " · distance" : ""}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-600" />
          </div>
        ))}
      </div>

      {data.is_own ? (
        <div data-testid="wk-import-own" className="border border-neutral-800 p-4 text-center">
          <div className="text-sm text-neutral-300">This is your own workout — already in your library.</div>
          <Link to="/library" className="text-xs text-blue-500 uppercase tracking-widest mt-2 inline-block">Open Library</Link>
        </div>
      ) : (
        <Button
          data-testid="wk-import-cta"
          onClick={importIt}
          disabled={importing}
          className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12"
        >
          {importing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Add to my Library</>
          )}
        </Button>
      )}
    </div>
  );
}
